import React, { useState, useEffect } from 'react';
import { User, Shield, TrendingUp, RefreshCw, ChevronDown, ExternalLink, Gavel, Briefcase, MapPin } from 'lucide-react';
import api from '../services/api';

/* ── Compact Card (case detail sidebar) ─────────────────────────────── */

export const OpposingCounselCompact = ({ caseId }) => {
  const [attorneys, setAttorneys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get(`/attorneys/cases/${caseId}/opposing-counsel`);
        setAttorneys(resp.data || []);
      } catch (err) {
        console.error('Failed to load opposing counsel:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [caseId]);

  if (loading || attorneys.length === 0) return null;

  return (
    <div className="space-y-2">
      <h4 className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1">
        <Shield className="w-3 h-3" /> Opposing Counsel
      </h4>
      {attorneys.map(atty => (
        <CompactCard key={atty.id} atty={atty} />
      ))}
    </div>
  );
};

const CompactCard = ({ atty }) => {
  const [expanded, setExpanded] = useState(false);
  const outcomes = atty.motion_outcomes || {};
  const patterns = atty.filing_patterns || {};
  const wins = outcomes.granted || 0;
  const losses = outcomes.denied || 0;

  const freqColor = {
    aggressive: 'text-red-400',
    moderate: 'text-yellow-400',
    passive: 'text-green-400',
  };

  return (
    <div className="bg-gray-900/60 rounded-lg border border-gray-700 p-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium text-gray-200">{atty.name}</div>
          <div className="text-xs text-gray-500">
            {atty.firm || 'Unknown Firm'}
            {atty.bar_number && <span className="ml-2">#{atty.bar_number}</span>}
          </div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="text-gray-600 hover:text-gray-400">
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      <div className="flex items-center gap-3 mt-2">
        {(wins > 0 || losses > 0) && (
          <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-300">
            {wins}W / {losses}L on motions
          </span>
        )}
        {patterns.filing_frequency && (
          <span className={`text-[10px] capitalize ${freqColor[patterns.filing_frequency] || 'text-gray-500'}`}>
            {patterns.filing_frequency} filer
          </span>
        )}
      </div>

      {expanded && atty.intel_summary && (
        <p className="text-xs text-gray-400 mt-2 leading-relaxed">
          {atty.intel_summary.split('\n')[0]}
        </p>
      )}
    </div>
  );
};

/* ── Full Profile Page ──────────────────────────────────────────────── */

export const OpposingCounselProfile = ({ profileId, onClose }) => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const resp = await api.get(`/attorneys/opposing/${profileId}`);
      setProfile(resp.data);
    } catch (err) {
      console.error('Failed to load profile:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfile(); }, [profileId]);

  const handleRegenerate = async () => {
    try {
      setRegenerating(true);
      await api.post(`/attorneys/opposing/${profileId}/regenerate-summary`);
      fetchProfile();
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setRegenerating(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-500 animate-pulse">Loading profile...</div>;
  if (!profile) return <div className="p-6 text-gray-500">Profile not found.</div>;

  const outcomes = profile.motion_outcomes || {};
  const patterns = profile.filing_patterns || {};
  const fingerprint = profile.argument_fingerprint || {};
  const totalMotions = outcomes.total_motions || 0;
  const winRate = totalMotions > 0 ? Math.round((outcomes.granted || 0) / totalMotions * 100) : 0;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-200">{profile.name}</h2>
          <div className="text-sm text-gray-400">{profile.firm || 'Unknown Firm'}</div>
          <div className="text-xs text-gray-500 mt-1 flex items-center gap-3">
            {profile.bar_number && <span>Bar #{profile.bar_number} ({profile.state_bar})</span>}
            {profile.practice_areas?.length > 0 && <span>{profile.practice_areas.join(', ')}</span>}
            {profile.first_seen_date && <span>Since {profile.first_seen_date}</span>}
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700 text-center">
          <div className="text-lg font-bold text-teal-400">{profile.case_count}</div>
          <div className="text-xs text-gray-500">Cases</div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700 text-center">
          <div className="text-lg font-bold text-amber-400">{totalMotions}</div>
          <div className="text-xs text-gray-500">Motions Filed</div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700 text-center">
          <div className={`text-lg font-bold ${winRate >= 50 ? 'text-red-400' : 'text-green-400'}`}>{winRate}%</div>
          <div className="text-xs text-gray-500">Win Rate</div>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700 text-center">
          <div className="text-lg font-bold text-gray-300 capitalize">{patterns.filing_frequency || '—'}</div>
          <div className="text-xs text-gray-500">Filing Pattern</div>
        </div>
      </div>

      {/* Motion outcomes by type */}
      {outcomes.by_type?.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Motion Outcomes by Type</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-700">
                <th className="text-left py-1">Motion Type</th>
                <th className="text-center py-1">Filed</th>
                <th className="text-center py-1">Granted</th>
                <th className="text-center py-1">Denied</th>
                <th className="text-center py-1">Withdrawn</th>
              </tr>
            </thead>
            <tbody>
              {outcomes.by_type.map((bt, i) => (
                <tr key={i} className="border-b border-gray-800/50">
                  <td className="py-1.5 text-gray-300">{bt.motion_type}</td>
                  <td className="py-1.5 text-center text-gray-400">{bt.filed || 0}</td>
                  <td className="py-1.5 text-center text-green-400">{bt.granted || 0}</td>
                  <td className="py-1.5 text-center text-red-400">{bt.denied || 0}</td>
                  <td className="py-1.5 text-center text-gray-500">{bt.withdrawn || 0}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Argument fingerprint */}
      {(fingerprint.themes?.length > 0 || fingerprint.legal_theories?.length > 0) && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Argument Fingerprint</h4>
          {fingerprint.themes?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {fingerprint.themes.map((t, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-900/30 text-purple-300 border border-purple-800/30">{t}</span>
              ))}
            </div>
          )}
          {fingerprint.legal_theories?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {fingerprint.legal_theories.map((t, i) => (
                <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-blue-900/30 text-blue-300 border border-blue-800/30">{t}</span>
              ))}
            </div>
          )}
          {fingerprint.patterns && (
            <p className="text-xs text-gray-400 mt-1">{fingerprint.patterns}</p>
          )}
        </div>
      )}

      {/* Judges */}
      {profile.judges_appeared_before?.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Judges Appeared Before</h4>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-500 border-b border-gray-700">
                <th className="text-left py-1">Judge</th>
                <th className="text-left py-1">Court</th>
                <th className="text-center py-1">Cases</th>
              </tr>
            </thead>
            <tbody>
              {profile.judges_appeared_before.map((j, i) => (
                <tr key={i} className="border-b border-gray-800/50">
                  <td className="py-1.5 text-gray-300">{j.judge_name}</td>
                  <td className="py-1.5 text-gray-400">{j.court}</td>
                  <td className="py-1.5 text-center text-gray-400">{j.case_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Case history */}
      {profile.cases?.length > 0 && (
        <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
          <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Case History ({profile.cases.length})</h4>
          <div className="space-y-1">
            {profile.cases.map((c, i) => (
              <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-gray-800/50 last:border-0">
                <span className="text-gray-300">{c.case_style || c.case_id}</span>
                {c.outcome_if_known && (
                  <span className="text-gray-500">{c.outcome_if_known}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Intel summary */}
      <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs font-semibold text-gray-500 uppercase">Intel Summary</h4>
          <button
            onClick={handleRegenerate}
            disabled={regenerating}
            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-teal-300 transition"
          >
            <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
            Regenerate
          </button>
        </div>
        <p className="text-sm text-gray-300 whitespace-pre-line leading-relaxed">
          {profile.intel_summary || 'No summary generated yet. Click Regenerate to create one.'}
        </p>
      </div>
    </div>
  );
};

export default OpposingCounselCompact;
